import React from 'react'
import { Field } from 'redux-form'

import { fieldAdapter, FieldProps } from '../../utils/field'
import { notNullNorUndefined, getIntegerOrNull } from '../../utils/mix'
import { intl } from '../../hoc'

import styles from './styles.scss'

interface Props {
  name: string
  testid: string
  messages?: {
    label?: string
    placeholder?: string
    notice?: string
    warning?: string
    error?: string
  }
  rangeFrom: number
  rangeFromSuffix?: string
  rangeTo: number
  rangeToSuffix?: string
  steps?: number[]
  hideSteps?: boolean
  onChange: any
  [x: string]: any
}

interface IntlInterface {
  intl: {
    formatNumber: any
  }
}

interface State {
  leftPosition: number
  steps: any[]
}

export class Wrapper extends React.Component<Props & FieldProps & IntlInterface, State> {
  state: State = {
    leftPosition: 0,
    steps: [],
  }

  // eslint-disable-next-line react/sort-comp
  mouseIsDown: boolean = false
  lineOver: any = null
  component: any = null
  componentBounds: any = null
  selectedStep: any = null

  // eslint-disable-next-line react/sort-comp
  onChangeCallbackTimeoutRef: any = undefined

  componentDidMount(): void {
    window.addEventListener('resize', this.prepareComponentBounds)
    window.addEventListener('mouseup', this.setMouseUp)
    window.addEventListener('touchend', this.setMouseUp)
    document.addEventListener('mousemove', this.trackMouseMove)
    document.addEventListener('touchmove', this.trackTouchMove)

    this.prepareComponentBounds()

    this.init(this.props)
  }

  componentDidUpdate(prevProps: Readonly<Props & FieldProps & IntlInterface>): void {
    if (this.props.steps !== prevProps.steps) {
      this.init(this.props)
    }

    if (this.props.value && this.lineOver && !this.mouseIsDown) {
      const actualValue: any = getIntegerOrNull(this.props.value)

      this.state.steps.map((item: any) => {
        if (item.value === actualValue) {
          this.lineOver.style.width = `${item.position}%`
        }
        return false
      })
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.prepareComponentBounds)
    window.removeEventListener('mouseup', this.setMouseUp)
    window.removeEventListener('touchend', this.setMouseUp)
    document.removeEventListener('mousemove', this.trackMouseMove)
    document.removeEventListener('touchmove', this.trackTouchMove)
  }

  init = (props: Props) => {
    let steps = []

    if (
      notNullNorUndefined(props.rangeFrom) &&
      notNullNorUndefined(props.rangeTo) &&
      props.steps &&
      Array.isArray(props.steps)
    ) {
      steps = props.steps.map((value: number) => {
        const ret: any = {
          value,
          selected: false,
        }

        const range = props.rangeTo - props.rangeFrom
        const updatedValue = value - props.rangeFrom
        if (updatedValue <= 0) {
          ret.position = 0
        } else if (updatedValue > range) {
          ret.position = 100
        } else {
          ret.position = (100 / range) * updatedValue
        }

        return ret
      })
    }

    this.setState({
      steps,
    })
  }

  prepareComponentBounds = () => {
    if (this.component) {
      this.componentBounds = this.component.getBoundingClientRect()
    }
  }

  setMouseDown = (e: any) => {
    if (this.props.steps && this.props.steps.length) {
      this.mouseIsDown = true
      this.computePointerPosition(e, false)
    }
  }

  trackTouchMove = (e: any) => this.trackMove(e)

  trackMouseMove = (e: any) => {
    e.preventDefault()

    this.trackMove(e)
  }

  trackMove = (e: any) => {
    if (this.component && this.mouseIsDown) {
      const selectedStep = this.computePointerPosition(e, false)

      // setTimeout as a render optimization feature
      clearTimeout(this.onChangeCallbackTimeoutRef)
      this.onChangeCallbackTimeoutRef = setTimeout(() => {
        if (selectedStep && this.props.value !== selectedStep.value && this.props.onChange) {
          this.props.onChange(selectedStep.value)
        }
      }, 0)
    }
  }

  setMouseUp = (e: any) => {
    if (this.mouseIsDown) {
      this.mouseIsDown = false
      const step = this.computePointerPosition(e, true)

      this.props.onChange && step && this.props.onChange(step.value)
    }
  }

  computePointerPosition = (e: any, roundToStep: boolean) => {
    let touch
    if (e && e.changedTouches) {
      touch = e.changedTouches[0]
    } else if (e && e.originalEvent && e.originalEvent.touches) {
      touch = e.originalEvent.touches[0]
    }

    if (this.selectedStep) {
      this.selectedStep.selected = false
    }
    this.selectedStep = null

    if (this.component) {
      if (!this.componentBounds) {
        this.prepareComponentBounds()
      }
      const componentBounds = this.componentBounds

      const elementWidth: number = componentBounds.width
      const mousePositionInsideElement: number = (e.pageX || touch.pageX) - componentBounds.left

      let leftPosition: number = (100 / elementWidth) * mousePositionInsideElement

      if (leftPosition < 0) {
        leftPosition = 0
      } else if (leftPosition > 100) {
        leftPosition = 100
      }

      if (this.state.steps.length > 0) {
        this.selectedStep = this.findStep(leftPosition, this.state.steps)

        if (roundToStep && this.selectedStep !== null) {
          leftPosition = this.selectedStep.position
        }
      }

      if (this.lineOver) {
        this.lineOver.style.width = `${leftPosition}%`
      }
    }

    return this.selectedStep
  }

  findStep = (leftPosition: number, steps: any[]): any => {
    let selectedStep: any = null
    let stepsPart: any = null

    if (steps.length > 1) {
      const splitIndex = Math.floor(steps.length / 2)
      const leftSteps = steps.slice(0, splitIndex)
      const rightSteps = steps.slice(splitIndex, 100000)
      stepsPart = leftSteps
      if (leftPosition >= rightSteps[0].position) {
        stepsPart = rightSteps
      } else if (
        leftPosition > leftSteps[leftSteps.length - 1].position &&
        leftPosition < rightSteps[0].position
      ) {
        const stepOne = leftSteps[leftSteps.length - 1]
        const stepTwo = rightSteps[0]
        const absOne = Math.abs(leftPosition - stepOne.position)
        const absTwo = Math.abs(leftPosition - stepTwo.position)

        if (absOne < absTwo) {
          stepOne.selected = true
          selectedStep = stepOne
        } else {
          stepTwo.selected = true
          selectedStep = stepTwo
        }
      }
    } else if (steps.length === 1) {
      selectedStep = steps[0]
    }

    if (selectedStep) {
      return selectedStep
    }
    if (stepsPart) {
      return this.findStep(leftPosition, stepsPart)
    }

    return null
  }

  render() {
    const rangeOptions: any = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }

    return (
      <div
        className={styles.Slider}
        onMouseDown={this.setMouseDown}
        onTouchStart={this.setMouseDown}
        ref={self => {
          this.component = self
        }}
        role="presentation">
        <div className={styles.lineContainer}>
          <div className={styles.line} />
          <div
            className={styles.lineOver}
            ref={self => {
              this.lineOver = self
            }}
          />
          {!this.props.hideSteps &&
            this.state.steps.map((step: any) => (
              <div
                className={styles.breakpoint}
                key={step.value}
                style={{ left: `${step.position}%` }}
              />
            ))}
        </div>
        <div className={styles.info}>
          <span>
            {this.props.intl.formatNumber(this.props.rangeFrom, rangeOptions)}
            {this.props.rangeFromSuffix && <span>&nbsp;{this.props.rangeFromSuffix}</span>}
          </span>
          <span>
            {this.props.intl.formatNumber(this.props.rangeTo, rangeOptions)}
            {this.props.rangeToSuffix && <span>&nbsp;{this.props.rangeToSuffix}</span>}
          </span>
        </div>
      </div>
    )
  }
}

export const renderSlider = intl(fieldAdapter(Wrapper))
export const Slider: React.FC<Props> = (props: Props) => {
  const onChange = (value: any) => {
    props.onChange && props.onChange(value)
  }
  return <Field {...props} onChange={onChange} component={renderSlider} />
}
export default Slider
